import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../insights/ContentState';

const ArticleCard = lazy(() => import('../insights/ArticleCard'));

function RecentInsights({ ArticleCardComponent = ArticleCard }) {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [requestKey, setRequestKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadRecentInsights() {
      setStatus('loading');

      try {
        const response = await fetch('/api/posts?page=1&limit=3', {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Recent insights request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data.posts)) {
          throw new Error('Recent insights response did not include a posts collection');
        }

        setPosts(data.posts);
        setStatus(data.posts.length > 0 ? 'ready' : 'empty');
      } catch (error) {
        if (error.name !== 'AbortError') {
          setPosts([]);
          setStatus('error');
        }
      }
    }

    loadRecentInsights();

    return () => controller.abort();
  }, [requestKey]);

  return (
    <section className="home-insights" aria-labelledby="home-insights-title">
      <div className="home-page__section-inner">
        <div className="home-insights__header">
          <div>
            <p className="home-page__eyebrow">Recent insights</p>
            <h2 className="home-insights__title" id="home-insights-title">
              Ideas from the field.
            </h2>
          </div>
          <Link className="home-page__text-link home-insights__all-link" to="/insights">
            View all insights
            <span aria-hidden="true">→</span>
          </Link>
        </div>

        {status === 'loading' && (
          <LoadingState
            className="home-insights__state"
            message="Gathering the latest writing from Vagelis."
          />
        )}

        {status === 'error' && (
          <ErrorState
            action={(
              <button type="button" onClick={() => setRequestKey(key => key + 1)}>
                Try again
              </button>
            )}
            className="home-insights__state"
            message="The latest writing could not be loaded. You can try the request again."
          />
        )}

        {status === 'empty' && (
          <EmptyState
            action={<Link to="/insights">Browse the Insights archive</Link>}
            className="home-insights__state"
          />
        )}

        {status === 'ready' && (
          <Suspense
            fallback={(
              <LoadingState
                className="home-insights__state"
                message="Preparing the latest writing for display."
                title="Loading recent insights"
              />
            )}
          >
            <div className="home-insights__collection">
              <ArticleCardComponent
                className="home-insights__lead-card"
                headingLevel={3}
                post={posts[0]}
                variant="featured"
              />
              {posts.length > 1 && (
                <div className="home-insights__secondary">
                  {posts.slice(1).map(post => (
                    <ArticleCardComponent
                      className="home-insights__card"
                      headingLevel={3}
                      imageLayout="home-secondary"
                      key={post.id}
                      post={post}
                    />
                  ))}
                </div>
              )}
            </div>
          </Suspense>
        )}
      </div>
    </section>
  );
}

export default RecentInsights;
